import { FC, useEffect } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { Spin } from 'antd'

import { APP_STATUS } from 'src/constants/common'
import * as ROUTES from 'src/constants/routes'
import { getConversationClient } from 'src/services/conversations/client'
import { initNativePush } from 'src/services/notification/pushNative'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { getConversationAccessToken, getSessionStatus } from 'src/store/slices/account'
import { isCapacitor } from 'src/utils/capacitor'

interface Props {
  redirectPath?: string
}

export const ProtectedRoute: FC<Props> = ({ redirectPath = ROUTES.LOGIN }) => {
  const dispatch = useAppDispatch()
  const account = useAppSelector((state) => state.account)

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        console.log('ProtectedRoute: Checking session status...')
        const result = await dispatch(getSessionStatus())
        console.log('ProtectedRoute: Session status result:', result)
      } catch (e) {
        console.log('ProtectedRoute: Session status error:', e)
      }
    }

    // Only fetch if we haven't already checked session status
    if (account.getSessionStatus.status === APP_STATUS.INIT) {
      void fetchCurrentUser()
    }
  }, [dispatch, account.getSessionStatus.status])

  useEffect(() => {
    console.log('ProtectedRoute: Account state changed:', {
      isLogin: account.isLogin,
      sessionStatus: account.getSessionStatus.status,
      error: account.getSessionStatus.error,
    })
  }, [account.isLogin, account.getSessionStatus.status, account.getSessionStatus.error])

  useEffect(() => {
    const initializeServices = async () => {
      try {
        if (!account.isLogin) return

        const result = await dispatch(getConversationAccessToken())
        const accessToken = (result.payload as { accessToken: string })?.accessToken
        if (!accessToken) return

        if (isCapacitor()) {
          await initNativePush(getConversationClient(), {
            onTap: (data) => {
              const conversationSid = data?.conversation_sid as string | undefined
              if (conversationSid) {
                window.location.assign(`/app?conversationId=${conversationSid}`)
              }
            },
          })
        }
      } catch (error) {
        console.error('Failed to initialize services:', error)
      }
    }

    if (account.isLogin) {
      void initializeServices()
    }
  }, [account.isLogin, dispatch])

  // Show loading spinner while checking authentication status
  if (
    account.getSessionStatus.status === APP_STATUS.LOADING ||
    account.getSessionStatus.status === APP_STATUS.INIT
  ) {
    return (
      <Spin
        size="large"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        tip="Checking authentication..."
      />
    )
  }

  // If authentication check is complete, redirect to login if not authenticated
  if (
    account.getSessionStatus.status === APP_STATUS.DONE ||
    account.getSessionStatus.status === APP_STATUS.ERROR
  ) {
    return account.isLogin ? <Outlet /> : <Navigate to={redirectPath} replace={true} />
  }

  // Fallback - should not reach here
  return <Navigate to={redirectPath} replace={true} />
}
